'use client';

import Link from 'next/link';
import { EndpointResult, EndpointStatus } from './types';
import ResultBars from './ResultBars';
import StatusBadge from './StatusBadge';
import { prettyTimeAgo, nanoToMs } from './timeUtils';
import { stripProtocolSuffix } from './stringUtils';

interface Props {
  endpoint: EndpointStatus;
  showAvgResponseTime: boolean;
  onTooltip: (result: EndpointResult | null, x: number, y: number, persistent: boolean) => void;
}

function formatResponseTime(results: EndpointResult[], showAvg: boolean): string {
  if (!results.length) return 'N/A';
  const durations = results.filter((r) => r.duration > 0).map((r) => nanoToMs(r.duration));
  if (!durations.length) return 'N/A';
  if (showAvg) {
    const avg = Math.round(durations.reduce((s, d) => s + d, 0) / durations.length);
    return `~${avg}ms`;
  }
  const min = Math.min(...durations);
  const max = Math.max(...durations);
  return min === max ? `${min}ms` : `${min}–${max}ms`;
}

export default function EndpointCard({ endpoint, showAvgResponseTime, onTooltip }: Props) {
  const last = endpoint.results[endpoint.results.length - 1];
  const status = last ? (last.success ? 'healthy' : 'unhealthy') : 'unknown';
  const hostname = last?.hostname || null;

  const oldest = endpoint.results.length
    ? prettyTimeAgo(endpoint.results[Math.max(0, endpoint.results.length - 50)].timestamp)
    : '';
  const newest = endpoint.results.length
    ? prettyTimeAgo(endpoint.results[endpoint.results.length - 1].timestamp)
    : '';

  return (
    <Link
      href={`/node-status/${encodeURIComponent(endpoint.key)}`}
      className="rounded-xl border border-white/10 bg-bg-card transition-all hover:border-white/20 hover:bg-bg-elevated w-full block px-4 py-3"
    >
      {/* Mobile: stacked layout — name + badge row, then bars */}
      <div className="flex items-start justify-between gap-2 mb-2 sm:hidden">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white truncate">{stripProtocolSuffix(endpoint.name)}</p>
          <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
            {endpoint.group && <span className="truncate">{endpoint.group}</span>}
            {endpoint.group && hostname && <span>•</span>}
            {hostname && <span className="truncate max-w-[120px]">{hostname}</span>}
          </div>
        </div>
        <div className="shrink-0">
          <StatusBadge status={status} />
        </div>
      </div>

      {/* Mobile: bars + timestamps */}
      <div className="sm:hidden">
        <ResultBars results={endpoint.results} onTooltip={onTooltip} />
        <div className="flex items-center justify-between mt-1 text-xs text-text-muted">
          <span>{oldest}</span>
          <span className="flex items-center gap-2">
            <span>{formatResponseTime(endpoint.results, showAvgResponseTime)}</span>
            <span>{newest}</span>
          </span>
        </div>
      </div>

      {/* Desktop: horizontal row */}
      <div className="hidden sm:flex items-center gap-4">
        {/* Left: name + meta */}
        <div className="w-48 shrink-0 min-w-0">
          <p className="text-sm font-semibold text-white truncate" title={endpoint.name}>
            {stripProtocolSuffix(endpoint.name)}
          </p>
          <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
            {endpoint.group && <span className="truncate">{endpoint.group}</span>}
            {endpoint.group && hostname && <span>•</span>}
            {hostname && <span className="truncate">{hostname}</span>}
          </div>
        </div>

        {/* Middle: bars + timestamps */}
        <div className="flex-1 min-w-0">
          <ResultBars results={endpoint.results} onTooltip={onTooltip} />
          <div className="flex items-center justify-between mt-1 text-xs text-text-muted">
            <span>{oldest}</span>
            <span>{newest}</span>
          </div>
        </div>

        {/* Right: badge + response time */}
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <StatusBadge status={status} />
          <span className="text-xs text-text-muted">{formatResponseTime(endpoint.results, showAvgResponseTime)}</span>
        </div>
      </div>
    </Link>
  );
}
